import { LayoutDashboard, Files, Heart, Zap, Trash2, PlayCircle, Sparkles, LogOut, Settings } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from './ui/sheet';
import { useAuth } from '../../lib/AuthContext';
import { useTheme } from 'next-themes';
import { useSubscription } from '../hooks/useSubscription';
import { PricingDialog } from './pricing-dialog';
import { useState } from 'react';

interface MobileMenuProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    activeNav?: string;
    onNavigate?: (nav: string) => void;
    onQuickRender?: () => void;
}

export function MobileMenu({ open, onOpenChange, activeNav, onNavigate, onQuickRender }: MobileMenuProps) {
    const [isPricingOpen, setIsPricingOpen] = useState(false);
    const { user, signOut, profile: authProfile } = useAuth();
    const { theme, setTheme } = useTheme();
    const isDarkMode = theme === 'dark';

    const {
        creditsAvailable: subCreditsAvailable,
        creditsLimit,
        planDisplayName,
    } = useSubscription();

    const creditsAvailable = authProfile?.available_credits ?? subCreditsAvailable;
    const subscriptionPlan = planDisplayName.toUpperCase();

    const profile = authProfile ? {
        firstName: authProfile.first_name,
        lastName: authProfile.last_name,
        avatarUrl: authProfile.avatar_url,
    } : null;

    const userEmail = user?.email || 'user@example.com';
    const displayName = profile?.firstName && profile?.lastName
        ? `${profile.firstName} ${profile.lastName}`
        : profile?.firstName || userEmail;
    const userInitials = profile?.firstName
        ? profile.firstName[0].toUpperCase() + (profile.lastName?.[0]?.toUpperCase() || '')
        : userEmail.slice(0, 2).toUpperCase();

    const handleLogout = async () => {
        onOpenChange(false);
        await signOut();
    };

    const handleNavigate = (nav: string) => {
        onOpenChange(false);
        onNavigate?.(nav);
    };

    const handleQuickRender = () => {
        onOpenChange(false);
        onQuickRender?.();
    };

    return (
        <>
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent side="left" className="w-[280px] p-0">
                    <SheetHeader className="px-6 py-5 border-b border-border">
                        <SheetTitle className="text-[24px] font-bold tracking-tight" style={{ fontFamily: 'Clash Grotesk, sans-serif' }}>
                            WYSKO
                        </SheetTitle>
                    </SheetHeader>

                    <div className="flex flex-col h-[calc(100%-80px)] overflow-y-auto">
                        {/* Primary Navigation - Same as bottom bar */}
                        <nav className="px-4 py-4 space-y-2 border-b border-border">
                            <button
                                onClick={handleQuickRender}
                                className="w-full flex items-center gap-3 text-left px-4 py-3 rounded-lg text-[14px] font-medium bg-primary text-primary-foreground"
                            >
                                <Zap className="w-5 h-5" strokeWidth={2} />
                                Szybki Render
                            </button>

                            <button
                                onClick={() => handleNavigate('properties')}
                                className={`w-full flex items-center gap-3 text-left px-4 py-3 rounded-lg text-[14px] font-medium transition-colors ${activeNav === 'properties'
                                    ? 'bg-muted text-foreground'
                                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                    }`}
                            >
                                <LayoutDashboard className="w-5 h-5" strokeWidth={1.5} />
                                Nieruchomości
                            </button>

                            <button
                                onClick={() => handleNavigate('allphotos')}
                                className={`w-full flex items-center gap-3 text-left px-4 py-3 rounded-lg text-[14px] font-medium transition-colors ${activeNav === 'allphotos'
                                    ? 'bg-muted text-foreground'
                                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                    }`}
                            >
                                <Files className="w-5 h-5" strokeWidth={1.5} />
                                Wszystkie pliki
                            </button>

                            <button
                                onClick={() => handleNavigate('favorites')}
                                className={`w-full flex items-center gap-3 text-left px-4 py-3 rounded-lg text-[14px] font-medium transition-colors ${activeNav === 'favorites'
                                    ? 'bg-muted text-foreground'
                                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                    }`}
                            >
                                <Heart className="w-5 h-5" strokeWidth={1.5} />
                                Ulubione
                            </button>
                        </nav>

                        {/* Secondary Navigation */}
                        <nav className="flex-1 px-4 py-4 space-y-2">
                            <button
                                onClick={() => handleNavigate('trash')}
                                className={`w-full flex items-center gap-3 text-left px-4 py-3 rounded-lg text-[14px] font-medium transition-colors ${activeNav === 'trash'
                                    ? 'bg-muted text-foreground'
                                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                    }`}
                            >
                                <Trash2 className="w-5 h-5" strokeWidth={1.5} />
                                Kosz
                            </button>

                            <button
                                onClick={() => handleNavigate('tutorials')}
                                className="w-full flex items-center gap-3 text-left px-4 py-3 rounded-lg text-[14px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                            >
                                <PlayCircle className="w-5 h-5" strokeWidth={1.5} />
                                Tutoriale
                            </button>

                            <button
                                onClick={() => handleNavigate('inspirations')}
                                className="w-full flex items-center gap-3 text-left px-4 py-3 rounded-lg text-[14px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                            >
                                <Sparkles className="w-5 h-5" strokeWidth={1.5} />
                                Inspiracje
                            </button>

                            <button
                                onClick={() => handleNavigate('settings')}
                                className={`w-full flex items-center gap-3 text-left px-4 py-3 rounded-lg text-[14px] font-medium transition-colors ${activeNav === 'settings'
                                    ? 'bg-muted text-foreground'
                                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                    }`}
                            >
                                <Settings className="w-5 h-5" strokeWidth={1.5} />
                                Ustawienia
                            </button>
                        </nav>

                        {/* Footer */}
                        <div className="px-4 pb-6 space-y-4 border-t border-border pt-4">
                            {/* Credits Widget */}
                            <div className={`px-4 py-3 rounded-lg border ${creditsAvailable === 0
                                ? 'bg-red-500/5 border-red-500/50'
                                : 'bg-muted/30 border-border/50'
                                }`}>
                                <div className="text-[9px] text-muted-foreground uppercase tracking-[0.1em] mb-1">
                                    {subscriptionPlan}
                                </div>
                                <div className={`text-[15px] font-semibold mb-2 ${creditsAvailable === 0 ? 'text-red-500' : ''}`}>
                                    {creditsAvailable} / {creditsLimit === 0 ? '∞' : creditsLimit}
                                </div>
                                <div className="w-full h-1 bg-muted rounded-full overflow-hidden mb-2">
                                    <div
                                        className={`h-full transition-all duration-300 ${creditsAvailable === 0 ? 'bg-red-500' :
                                            (creditsLimit > 0 && (creditsAvailable / creditsLimit) < 0.2) ? 'bg-red-500' :
                                                (creditsLimit > 0 && (creditsAvailable / creditsLimit) < 0.4) ? 'bg-yellow-500' :
                                                    'bg-green-500'
                                            }`}
                                        style={{ width: creditsLimit > 0 ? `${Math.min((creditsAvailable / creditsLimit) * 100, 100)}%` : '100%' }}
                                    />
                                </div>
                                <button
                                    className="text-[11px] text-muted-foreground hover:text-foreground transition-colors underline"
                                    onClick={() => setIsPricingOpen(true)}
                                >
                                    Zwiększ limit
                                </button>
                            </div>

                            {/* Dark Mode Toggle */}
                            <div className="flex items-center justify-between px-2">
                                <Label htmlFor="mobile-dark-mode" className="text-[13px]">
                                    Tryb ciemny
                                </Label>
                                <Switch
                                    id="mobile-dark-mode"
                                    checked={isDarkMode}
                                    onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
                                />
                            </div>

                            {/* User Profile */}
                            <div className="flex items-center gap-3 px-2 py-2">
                                <Avatar className="w-9 h-9 border border-border">
                                    {profile?.avatarUrl && <AvatarImage src={profile.avatarUrl} />}
                                    <AvatarFallback className="bg-primary text-primary-foreground text-[11px] font-medium">
                                        {userInitials}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[13px] font-medium truncate">{displayName}</div>
                                    <div className="text-[10px] text-muted-foreground truncate">{userEmail}</div>
                                </div>
                            </div>

                            {/* Logout */}
                            <Button
                                variant="ghost"
                                className="w-full justify-start text-[13px] text-muted-foreground hover:text-foreground"
                                onClick={handleLogout}
                            >
                                <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                Wyloguj
                            </Button>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>

            <PricingDialog open={isPricingOpen} onOpenChange={setIsPricingOpen} />
        </>
    );
}
